import { useState, useEffect } from 'react';

export interface OnlineStatus {
  isOnline: boolean;
  wasOffline: boolean;
  effectiveConnectionType?: string;
}

/**
 * Hook to track online/offline status with connection quality
 */
export const useOnlineStatus = (): OnlineStatus => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [effectiveConnectionType, setEffectiveConnectionType] = useState<string>();

  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Network: Connection restored');
      setIsOnline(true);
      setWasOffline(true);

      // Clear the "was offline" flag after a delay
      setTimeout(() => setWasOffline(false), 3000);
    };

    const handleOffline = () => {
      console.log('📴 Network: Connection lost');
      setIsOnline(false);
    };

    // Check connection type if available (experimental API)
    const updateConnectionType = () => {
      const connection = (navigator as any).connection ||
                        (navigator as any).mozConnection ||
                        (navigator as any).webkitConnection;

      if (connection) {
        setEffectiveConnectionType(connection.effectiveType);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for connection changes
    const connection = (navigator as any).connection ||
                      (navigator as any).mozConnection ||
                      (navigator as any).webkitConnection;

    if (connection) {
      connection.addEventListener('change', updateConnectionType);
      updateConnectionType();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if (connection) {
        connection.removeEventListener('change', updateConnectionType);
      }
    };
  }, []);

  return {
    isOnline,
    wasOffline,
    effectiveConnectionType,
  };
};
